import React, { useEffect, useMemo, useState } from "react";

function norm(s) {
  return (s || "")
    .toString()
    .normalize("NFKC")
    .trim()
    .toLowerCase();
}

function App() {
  const [channels, setChannels] = useState([]);
  const [loadingErr, setLoadingErr] = useState("");

  // UI state
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [seedOnly, setSeedOnly] = useState(false);

  useEffect(() => {
    fetch("/channel_registry.json", { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        const list = Array.isArray(data?.channels) ? data.channels : [];
        setChannels(list);
        setLoadingErr("");
      })
      .catch((err) => {
        console.error("채널 로딩 실패:", err);
        setLoadingErr(
          "채널 레지스트리 로딩 실패: public/channel_registry.json 확인 필요"
        );
      });
  }, []);

  const typeOptions = useMemo(() => {
    const set = new Set();
    channels.forEach((c) => {
      if (c?.channel_type) set.add(c.channel_type);
    });
    return ["ALL", ...Array.from(set).sort((a, b) => a.localeCompare(b, "ko"))];
  }, [channels]);

  const filtered = useMemo(() => {
    const nq = norm(q);
    return channels
      .filter((c) => {
        if (seedOnly && !c?.seed) return false;
        if (typeFilter !== "ALL" && c?.channel_type !== typeFilter) return false;

        if (!nq) return true;

        const hay = [
          c?.title,
          c?.channel_type,
          c?.link,
          c?.slug, // 있을 수도 있음
        ]
          .filter(Boolean)
          .map(norm)
          .join(" ");

        return hay.includes(nq);
      })
      .sort((a, b) => {
        // title이 "01. ..." 처럼 시작하면 자연정렬 느낌으로
        const ta = a?.title || "";
        const tb = b?.title || "";
        return ta.localeCompare(tb, "ko");
      });
  }, [channels, q, typeFilter, seedOnly]);

  const counts = useMemo(() => {
    const total = channels.length;
    const seed = channels.filter((c) => c?.seed).length;
    return { total, seed, shown: filtered.length };
  }, [channels, filtered]);

  // channel page 링크: public/channels 아래 정적 페이지로 연결
  // build_channels.py가 생성한 구조: channels/<폴더>/index.html
  // React에서는 /channels/<폴더>/index.html 로 접근 가능
  const channelPageHref = (c) => {
    // c.dir 또는 c.folder 같은 필드가 있으면 그걸 우선 사용
    // 없으면 title 기반으로 이미 만들어둔 폴더명이 title과 동일하게 생성된 상태(사용자 환경)
    // -> registry에 폴더명이 없으면 "채널 페이지" 링크가 100% 확정되지 않으니,
    //    일단 가장 흔한 케이스: build_channels.py가 "channels\<title>" 형태로 생성한 경우를 지원.
    const folder =
      c?.folder || c?.dir || c?.path || c?.title || "";
    if (!folder) return "";
    return `/channels/${encodeURIComponent(folder)}/index.html`;
  };

  return (
    <div style={{ padding: "24px", fontFamily: "sans-serif", maxWidth: 980 }}>
      <h1 style={{ marginTop: 0 }}>
        📺 GPTube 채널 목록
      </h1>

      <div style={{ marginBottom: 12, color: "#444" }}>
        전체 {counts.total}개 · Seed {counts.seed}개 · 표시 {counts.shown}개
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 220px 160px",
          gap: "10px",
          alignItems: "center",
          marginBottom: 18,
        }}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="검색: 제목/유형/링크"
          style={{
            padding: "10px",
            borderRadius: 8,
            border: "1px solid #ccc",
          }}
        />

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{
            padding: "10px",
            borderRadius: 8,
            border: "1px solid #ccc",
            background: "white",
          }}
        >
          {typeOptions.map((t) => (
            <option key={t} value={t}>
              {t === "ALL" ? "유형: 전체" : t}
            </option>
          ))}
        </select>

        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="checkbox"
            checked={seedOnly}
            onChange={(e) => setSeedOnly(e.target.checked)}
          />
          Seed만 보기
        </label>
      </div>

      {loadingErr && (
        <div
          style={{
            background: "#ffecec",
            border: "1px solid #ffb3b3",
            padding: 12,
            borderRadius: 8,
            marginBottom: 14,
            color: "#8a0000",
          }}
        >
          {loadingErr}
          <div style={{ marginTop: 8, fontSize: 13 }}>
            체크: <code>client/public/channel_registry.json</code> 존재 여부
          </div>
        </div>
      )}

      {filtered.length === 0 && (
        <div style={{ color: "#666" }}>표시할 채널이 없습니다.</div>
      )}

      <ol style={{ paddingLeft: 18, lineHeight: 1.5 }}>
        {filtered.map((c, i) => {
          const page = channelPageHref(c);
          return (
            <li key={i} style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700 }}>{c?.title}</div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {c?.link && (
                  <a href={c.link} target="_blank" rel="noreferrer">
                    GPT 바로가기
                  </a>
                )}

                {page && (
                  <a href={page} target="_blank" rel="noreferrer">
                    채널 페이지
                  </a>
                )}

                {c?.seed && (
                  <span
                    style={{
                      fontSize: 12,
                      padding: "2px 8px",
                      borderRadius: 999,
                      border: "1px solid #bbb",
                      color: "#333",
                    }}
                  >
                    SEED
                  </span>
                )}
              </div>

              <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
                유형: {c?.channel_type || "-"}
              </div>
            </li>
          );
        })}
      </ol>

      <div style={{ marginTop: 24, fontSize: 12, color: "#777" }}>
        아코어 운영 메모: <code>/channel_registry.json</code> 과{" "}
        <code>/channels/</code> 는 <code>client/public</code> 아래에 있어야 합니다.
      </div>
    </div>
  );
}

export default App;
